// Configuração centralizada de ambiente
// Este arquivo gerencia todas as variáveis de ambiente da aplicação

export interface EnvironmentConfig {
  api: {
    baseURL: string;
    timeout: number;
    retryAttempts: number;
    retryDelay: number;
  };
  app: {
    title: string;
    version: string;
    environment: string;
  };
  features: {
    enableDebugMode: boolean;
    enableAnalytics: boolean;
  };
}

// Configuração para desenvolvimento
const developmentConfig: EnvironmentConfig = {
  api: {
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost/api',
    timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000'),
    retryAttempts: parseInt(import.meta.env.VITE_API_RETRY_ATTEMPTS || '3'),
    retryDelay: parseInt(import.meta.env.VITE_API_RETRY_DELAY || '1000'),
  },
  app: {
    title: import.meta.env.VITE_APP_TITLE || 'CoffeeIn Admin - Dev',
    version: import.meta.env.VITE_APP_VERSION || '1.0.0',
    environment: 'development',
  },
  features: {
    enableDebugMode: import.meta.env.VITE_ENABLE_DEBUG === 'true',
    enableAnalytics: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
  },
};

// Configuração para produção
const productionConfig: EnvironmentConfig = {
  api: {
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost/api',
    // baseURL: import.meta.env.VITE_API_URL || 'https://api.coffeein.club/api',
    timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000'),
    retryAttempts: parseInt(import.meta.env.VITE_API_RETRY_ATTEMPTS || '3'),
    retryDelay: parseInt(import.meta.env.VITE_API_RETRY_DELAY || '1000'),
  },
  app: {
    title: import.meta.env.VITE_APP_TITLE || 'CoffeeIn Admin',
    version: import.meta.env.VITE_APP_VERSION || '1.0.0',
    environment: 'production',
  },
  features: {
    enableDebugMode: false,
    enableAnalytics: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
  },
};

// Configuração para staging/teste
const stagingConfig: EnvironmentConfig = {
  api: {
    baseURL: import.meta.env.VITE_API_URL || 'https://api-staging.coffeein.club/api',
    timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000'),
    retryAttempts: parseInt(import.meta.env.VITE_API_RETRY_ATTEMPTS || '3'),
    retryDelay: parseInt(import.meta.env.VITE_API_RETRY_DELAY || '1000'),
  },
  app: {
    title: import.meta.env.VITE_APP_TITLE || 'CoffeeIn Admin - Staging',
    version: import.meta.env.VITE_APP_VERSION || '1.0.0',
    environment: 'staging',
  },
  features: {
    enableDebugMode: import.meta.env.VITE_ENABLE_DEBUG === 'true',
    enableAnalytics: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
  },
};

// Função para determinar o ambiente atual
function getCurrentEnvironment(): string {
  // Prioridade: VITE_NODE_ENV > import.meta.env.MODE > fallback
  return import.meta.env.VITE_NODE_ENV || import.meta.env.MODE || 'development';
}

// Função para obter a configuração baseada no ambiente
function getConfig(): EnvironmentConfig {
  const env = getCurrentEnvironment();
  
  switch (env) {
    case 'production':
      return productionConfig;
    case 'staging':
      return stagingConfig;
    case 'development':
    default:
      return developmentConfig;
  }
}

// Configuração atual da aplicação
export const config = getConfig();

// Função para validar se todas as variáveis necessárias estão definidas
export function validateEnvironment(): void {
  const requiredVars = ['VITE_API_URL'];
  const missingVars = requiredVars.filter(varName => !import.meta.env[varName]);
  
  if (missingVars.length > 0) {
    console.warn(`⚠️ Variáveis de ambiente ausentes: ${missingVars.join(', ')}`);
    console.warn('📖 Verifique a documentação para configurar as variáveis necessárias');
  }
  
  if (config.features.enableDebugMode) {
    console.log('🔧 Configuração do ambiente:', config);
  }
}

// Função para obter informações de debug do ambiente
export function getEnvironmentInfo(): Record<string, any> {
  return {
    currentEnv: getCurrentEnvironment(),
    config,
    envVars: {
      VITE_API_URL: import.meta.env.VITE_API_URL,
      VITE_APP_TITLE: import.meta.env.VITE_APP_TITLE,
      VITE_NODE_ENV: import.meta.env.VITE_NODE_ENV,
      MODE: import.meta.env.MODE,
    },
  };
}

// Exportar funções úteis
export { getCurrentEnvironment, getConfig };
